import { NavLink } from "react-router-dom";

import { SummaryCard } from "../components/SummaryCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";
import { DetailCardGrid, DetailMetricGrid } from "./DeepDiveParts";

export function FallbackPage() {
  const { data, loading, error } = useContent("fallback-page", api.getFallback);

  return (
    <PageContainer
      eyebrow="Deep Detail - Financials"
      title="Fallback detail for the Financials surface"
      lede="This route is the downside-case layer beneath Financials. It keeps fallback support visible for capital-protection review without letting the fallback frame compete with the main investor-facing financial surface."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading fallback summary...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? (
        <div className="stack-xl">
          <DetailMetricGrid
            items={[
              { title: "Fallback Paths", value: `${data.fallbackPaths.length}`, detail: "Final conclusion paths" },
              { title: "Processed Paths", value: `${data.processedFallbackPaths.length}`, detail: "Directional support set" },
              { title: "Limits", value: `${data.limits.length}`, detail: "What still needs hard economics" },
              { title: "Status", value: data.status, detail: "Fallback summary status" }
            ]}
          />

          <DetailCardGrid
            items={[
              ...data.fallbackPaths.map((item) => ({
                eyebrow: "Fallback Path",
                title: item.fallbackPath,
                description: item.currentBestRead,
                fields: [{ label: "Role in Project", value: item.roleInProject }]
              })),
              {
                eyebrow: "Long-Term Rental",
                title: "The clearest downside floor",
                description: data.longTermRentalRead
              },
              {
                eyebrow: "Build-to-Sell",
                title: "A secondary exit path still matters",
                description: data.buildToSellRead
              }
            ]}
          />

          <div className="grid-two">
            <SummaryCard eyebrow="Support" title="What currently supports the fallback case" description={data.overview}>
              <ul className="bullet-list">
                {data.supportBullets.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Processed Snapshot" title="Directional fallback references still in the stack" description={data.currentBestRead}>
              <ul className="bullet-list">
                {data.processedFallbackPaths.map((item) => (
                  <li key={item.fallbackPath} className="bullet-item">{`${item.fallbackPath}: ${item.currentBestRead} (${item.confidence})`}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <SummaryCard eyebrow="Limits" title="Why fallback still needs economic translation" description={data.conclusion}>
            <ul className="bullet-list">
              {data.limits.map((item) => (
                <li key={item} className="bullet-item">{item}</li>
              ))}
            </ul>
          </SummaryCard>

          <SummaryCard
            eyebrow="Parent Surface"
            title="Return to Financials for the full lead-case and downside read"
            description="Use this page when the fallback floor or exit paths need closer inspection. Use Financials for the integrated investor-facing economics posture."
          >
            <div className="row-wrap gap-sm overview-go-deeper-actions">
              <NavLink to="/financials" className="link-button link-button-secondary">
                Review Financials
              </NavLink>
              <NavLink to="/underwriting" className="link-button link-button-secondary">
                Open Underwriting
              </NavLink>
            </div>
          </SummaryCard>
        </div>
      ) : null}
    </PageContainer>
  );
}
